import { users } from "api/certified-api";


/**
 * Obtiene usuarios filtrados mediante query params.
 *
 * Permite obtener:
 * - Todos los usuarios: {}
 * - Un usuario por ID: { id: 5 }
 * - Un usuario por username: { username: 'master' }
 * - Usuarios por tipo (certified o candidates): { type: 'certified' }
 * - Usuarios por role_id: { role_id: 4 }
 *
 * @param {Object} filters - Objeto con filtros como id, username, type, role_id, etc.
 * @param {string} token - Token JWT.
 * @returns {Promise<Object>} Datos de los usuarios.
 */
export const fetchUsers = async (filters = {}, token) => {
	const query = new URLSearchParams(filters).toString();
	const url = query ? `/?${query}` : `/`;

	const response = await users.get(url, {
		headers: getHeaders(token)
	});
	return response.data;
};

/**
 * Crea un usuario en el sistema.
 *
 * Esta función unifica los tres casos disponibles en el backend:
 * - Usuario normal (admin, examiner, certified, etc.).
 * - Candidato individual (se envía correo automáticamente).
 * - Carga masiva de candidatos desde un archivo.
 *
 * El backend determina el comportamiento según las claves presentes en el objeto `data`.
 *
 * @param {Object} data - Datos para crear el usuario o cargar candidatos.
 * @param {string} token - Token JWT de autenticación.
 * @returns {Promise<Object>} Respuesta del servidor.
 */
export const createUser = async (data, token) => {
	const response = await users.post(`/`, data, {
		headers: getHeaders(token)
	});
	return response.data;
};

/**
 * Actualiza los datos básicos de un usuario.
 *
 * Esta función permite actualizar `name`, `lastname`, `role_id` y `status`
 * en una sola petición, conforme al nuevo backend unificado.
 *
 * @param {number|string} id - ID del usuario a actualizar.
 * @param {Object} data - Campos a actualizar (ej. name, lastname, role_id, status).
 * @param {string} token - Token JWT de autenticación.
 * @returns {Promise<Object>} Respuesta con mensaje y user_id.
 */
export const updateUser = async (id, data, token) => {
	const response = await users.put(`/${id}`, data, {
		headers: getHeaders(token)
	});
	return response.data;
};

/**
 * Actualiza la contraseña de un usuario.
 *
 * Si el usuario es un candidato (`role_id === 4`), el backend genera
 * automáticamente una contraseña nueva y envía un correo de notificación.
 * Para otros usuarios, se debe enviar el campo `password` manualmente.
 *
 * @param {number|string} id - ID del usuario.
 * @param {Object} data - Objeto con la clave `password` (solo si no es candidato).
 * @param {string} token - Token JWT de autenticación.
 * @returns {Promise<Object>} Respuesta con mensaje de éxito o error.
 */
export const updateUserPassword = async (id, data, token) => {
	const response = await users.put(`/${id}/password`, data, {
		headers: getHeaders(token)
	});
	return response.data;
};

/**
 * Elimina un usuario por ID.
 * @param {number|string} id - ID del usuario.
 * @param {string} token - Token JWT.
 * @returns {Promise<Object>} Resultado de la eliminación.
 */
export const deleteUserById = async (id, token) => {
	const response = await users.delete(`/${id}`, {
		headers: getHeaders(token)
	});
	return response.data;
};

/**
 * Genera los encabezados comunes para las peticiones.
 * @param {string} token - Token JWT.
 * @returns {Object} Headers estándar.
 */
const getHeaders = (token) => ({
	'Content-Type': 'application/json',
	'Accept': 'application/json',
	'x-access-token': token
});